/* eslint-disable @web-monorepo/marketplace-dates-imports -- this is the shim in question! */
import {
  parse,
  addDays,
  addHours,
  addMinutes,
  parseWithPattern as datesParseWithPattern,
  formatWithPattern as datesFormatWithPattern,
  isSame,
  startOf,
  subDays,
  overrideLocalTimezone,
  getLocalTimezoneOverride,
  enableGlobalTimezoneSupport,
  getTimezoneOffset,
} from "@web-monorepo/dates";

export * from "@web-monorepo/dates";

enableGlobalTimezoneSupport();

export function setDefaultTimezone(timezone: string | null) {
  overrideLocalTimezone(timezone);
}

export function getDefaultTimezone() {
  return getLocalTimezoneOverride() ?? getDeviceTimezone();
}

/** Display-friendly timezone name (e.g. "America/Los Angeles") */
export function getTimezoneText() {
  return getDefaultTimezone().replace("_", " ");
}

export function getUpcomingDays(options: { count: number; startOffset: number; timezone?: string }): Date[] {
  const { count, startOffset, timezone } = options;
  const tz = timezone ?? getDefaultTimezone();
  const today = startOf(new Date(), "day", tz);
  return Array.from({ length: count }, (_, i) => addDays(today, i + startOffset));
}

/**
 * Frontend-enforced minimum lead time for showing trial slots (2 hours).
 * Applied defensively on top of any backend trialAdvanceNotice filtering.
 * "Less than 2 hours" means we keep slots at/after the exact +2h boundary.
 * The constant lives here with the other date utilities; the functions that
 * apply it live in trialSlotWindow.ts (getTrialSlotWindow / isBeforeEarliestBookable).
 */
export const FRONTEND_MIN_TRIAL_LEAD_TIME_MS = 2 * 60 * 60 * 1000;

export function formatWithPattern(date: Date, pattern: string, options?: { timeZone?: string }): string {
  return datesFormatWithPattern(date, pattern === "PP" ? "MMMM dd, yyyy" : pattern, options);
}

export function formatDayOffRange(startTs: string, endTs?: string): string {
  const tz = getDefaultTimezone();
  const startMoment = parse(startTs);

  const startFormatted = formatWithPattern(startMoment, "MMMM d, yyyy", { timeZone: tz });

  if (!endTs) return startFormatted;

  const endMoment = parse(endTs);
  let endFormatted;

  if (isSame(endMoment, startOf(endMoment, "day", tz))) {
    endFormatted = formatWithPattern(subDays(endMoment, 1), "MMMM d, yyyy", { timeZone: tz });
  } else {
    endFormatted = formatWithPattern(endMoment, "MMMM d, yyyy", { timeZone: tz });
  }

  return endFormatted !== startFormatted ? `${startFormatted} - ${endFormatted}` : startFormatted;
}

export const getHourWithDiffHour = (hour: string, tzDiffHours: number) => {
  /*
    Receive hour in format h:mm A, e.g.: 1:30 PM
    limited hours from 12:00 AM to 11:30 PM
  */
  return formatWithPattern(addHours(parseWithPattern(`${hour}`, ["h:mm a"]), tzDiffHours), "h:mm a");
};

function getDeviceTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export const getLocalizedDateTime = (date?: string, time?: string, timezone?: string) => {
  const timezoneToUse = timezone ?? getDefaultTimezone();

  const parsedDate = date
    ? datesParseWithPattern(date, ["PP", "MMMM dd yyyy", "MMMM dd, yyyy"], new Date(), timezoneToUse)
    : new Date();

  return time ? datesParseWithPattern(time, ["h:mm a", "HH:mm"], parsedDate, timezoneToUse) : parsedDate;
};

export const parseIsoStringInTimeZone = (isoString: string, timezone: string) => {
  return parseWithPattern(isoString, "yyyy-MM-dd'T'HH:mm:ss.SSSX", undefined, timezone);
};

/**
 * Parse UTC time string (HH:mm:ssZ format) to hours and minutes
 * @param utcTime - UTC time string like "19:00:00Z"
 * @returns Object with hours and minutes, or null if invalid
 */
export const parseUtcTimeString = (utcTime: string): { hours: number; minutes: number } | null => {
  const match = utcTime.match(/^(\d{2}):(\d{2}):(\d{2})Z$/);
  if (!match || !match[1] || !match[2]) return null;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return { hours, minutes };
};

/**
 * Check if error is a date-related error
 */
export const isDateError = (err: unknown): boolean => {
  return (
    err instanceof Error &&
    (err.message.includes("Invalid time") || err.message.includes("Invalid Date") || err.name === "RangeError")
  );
};

export const parseWithPattern = (
  date: string | undefined,
  pattern: string | string[],
  referenceDate?: Date,
  timeZone?: string,
): Date => {
  // This is how moment behaves, but is not ideal:
  if (typeof date === "undefined") return new Date(NaN);

  // interop for a common pattern that is _close_:
  if (pattern === "PP") pattern = ["PP", "MMMM dd yyyy", "MMMM dd, yyyy"];

  return datesParseWithPattern(date, pattern, referenceDate, timeZone ?? getDefaultTimezone() ?? undefined);
};

export const getOffsetFromTimezone = (targetTimeZone: string) => {
  const userTimezone = getDefaultTimezone();
  const userOffset = getTimezoneOffset(userTimezone) / 60_000;
  const deviceOffset = getTimezoneOffset(targetTimeZone) / 60_000;
  return (userOffset - deviceOffset) / 60;
};

/**
 * Formats a Date object into a time range string for session display.
 * Creates a 25-minute session duration by adding 25 minutes to the start time.
 *
 * @param selectedDateTime - The start date/time of the session
 * @returns A formatted time range string (e.g. "2:00 PM - 2:25 PM") or undefined if no date provided
 */
export function formatTimeRange(selectedDateTime: Date | undefined): string | undefined {
  if (!selectedDateTime) return undefined;

  const startTime = formatWithPattern(selectedDateTime, "h:mm a");
  const endDateTime = addMinutes(selectedDateTime, 25);
  const endTime = formatWithPattern(endDateTime, "h:mm a");
  return `${startTime} - ${endTime}`;
}

/**
 * Formats a time range with smart AM/PM display and timezone support.
 * When both times are in the same period (AM or PM), the period is shown only once.
 * Always shows minutes (e.g., "8:00 - 9:00 AM" instead of "8 - 9 AM").
 *
 * @param startDate - Start time
 * @param endDate - End time
 * @param timezone - Optional IANA timezone (e.g., "America/Los_Angeles"). If not provided, uses browser's local timezone.
 * @returns Formatted time range (e.g., "6:00 - 6:25 PM" or "11:30 AM - 1:00 PM")
 *
 * @example
 * formatTimeRangeWithTimezone(new Date("2025-02-15T18:00:00Z"), new Date("2025-02-15T18:25:00Z"))
 * // Returns: "6:00 - 6:25 PM" (in browser's local timezone)
 *
 * @example
 * formatTimeRangeWithTimezone(new Date("2025-02-15T11:30:00Z"), new Date("2025-02-15T13:00:00Z"), "America/New_York")
 * // Returns: "11:30 AM - 1:00 PM" (in America/New_York timezone)
 */
export function formatTimeRangeWithTimezone(startDate: Date, endDate: Date, timezone?: string): string {
  return formatSmartTimeRange(startDate, endDate, { timezone, hideMinutesOnHour: false });
}

/**
 * Formats a time range intelligently, showing AM/PM only when necessary.
 * - If both times are in the same period (both AM or both PM), only shows period on end time
 * - If times span different periods (AM to PM), shows both periods
 * - Optionally hides :00 for times on the hour
 *
 * @param startDate - The start date/time
 * @param endDate - The end date/time
 * @param options - Optional configuration
 * @param options.timezone - Optional IANA timezone (e.g., "America/Los_Angeles")
 * @param options.hideMinutesOnHour - Whether to hide :00 for times on the hour (default: true)
 * @returns A formatted time range string (e.g. "8 - 9 AM", "11:45 AM - 12:10 PM")
 *
 * @example
 * formatSmartTimeRange(new Date('2024-01-01 08:00'), new Date('2024-01-01 09:00')) // "8 - 9 AM"
 * formatSmartTimeRange(new Date('2024-01-01 11:45'), new Date('2024-01-01 12:10')) // "11:45 AM - 12:10 PM"
 * formatSmartTimeRange(new Date('2024-01-01 14:30'), new Date('2024-01-01 15:00')) // "2:30 - 3 PM"
 * formatSmartTimeRange(new Date('2024-01-01 08:00'), new Date('2024-01-01 09:00'), { hideMinutesOnHour: false }) // "8:00 - 9:00 AM"
 */
export function formatSmartTimeRange(
  startDate: Date,
  endDate: Date,
  options?: { timezone?: string; hideMinutesOnHour?: boolean },
): string {
  const { timezone, hideMinutesOnHour = true } = options || {};

  // Format both times with full pattern (always include minutes and period)
  const startTimeFormatted = formatWithPattern(startDate, "h:mm a", { timeZone: timezone });
  const endTimeFormatted = formatWithPattern(endDate, "h:mm a", { timeZone: timezone });

  // Extract periods from the formatted strings
  const startPeriod = startTimeFormatted.match(/\s(AM|PM)$/)?.[1];
  const endPeriod = endTimeFormatted.match(/\s(AM|PM)$/)?.[1];
  const showStartPeriod = startPeriod !== endPeriod;

  // Optionally hide minutes for times on the hour (check formatted string)
  const formatTimeFinal = (timeStr: string) => {
    if (hideMinutesOnHour && timeStr.includes(":00 ")) {
      return timeStr.replace(":00 ", " ");
    }
    return timeStr;
  };

  const startTimeFinal = formatTimeFinal(startTimeFormatted);
  const endTimeFinal = formatTimeFinal(endTimeFormatted);

  // Remove period from start time if both periods are the same
  const startTime = showStartPeriod ? startTimeFinal : startTimeFinal.replace(" AM", "").replace(" PM", "");
  return `${startTime} - ${endTimeFinal}`;
}

export function findFirstAvailableDay(options: {
  slots: (string | Date)[] | undefined;
  startOffset: number;
  count: number;
  earliestBookableTime: Date;
  selectedDays?: string[];
  timezone?: string;
}): { day: Date; slotCount: number } | null {
  const { slots, selectedDays, timezone, startOffset, count, earliestBookableTime } = options;

  if (!slots?.length) return null;

  const tz = timezone ?? getDefaultTimezone();
  const slotDates = slots.map((slot) => (typeof slot === "string" ? new Date(slot) : slot));
  const upcomingDays = getUpcomingDays({ count, timezone: tz, startOffset });
  const earliestMs = earliestBookableTime.getTime();

  for (const day of upcomingDays) {
    // If there's a filter, check if this day matches
    if (selectedDays?.length) {
      const dayName = formatWithPattern(day, "EEEE", { timeZone: tz }).toLowerCase();
      if (!selectedDays.includes(dayName)) continue;
    }

    // Count slots for this day
    const dayStart = startOf(day, "day", tz);
    const dayEnd = addDays(dayStart, 1);

    const slotsOnThisDay = slotDates.filter((slotDate) => {
      const slotTime = slotDate.getTime();
      if (!(slotTime >= dayStart.getTime() && slotTime < dayEnd.getTime())) return false;
      if (slotTime < earliestMs) return false;
      return true;
    });

    // If this day has slots, return it
    if (slotsOnThisDay.length > 0) {
      return { day, slotCount: slotsOnThisDay.length };
    }
  }

  return null;
}
